import { useMemo } from 'react'

import type { UserResponse } from '#/shared/api/generated/auth/models'
import type { ShipmentTeamsResponse } from '#/shared/api/generated/shipment/models'
import { useListShipmentTeamsApiV1ShipmentsTeamsGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { useAuthStore } from '#/shared/lib/auth'
import { useDebouncedValue } from '#/shared/lib/hooks'
import type { UserOption } from '#/shared/ui/user-dropdown'
import type { ShipmentStage } from '../model/stages'

const USER_SEARCH_DEBOUNCE_MS = 250

type TeamMember = {
  id: string
  first_name?: string | null
  last_name?: string | null
  picture?: string | null
  count?: number | null
}

function toUserOption(user: TeamMember, teamName: string): UserOption | null {
  const name = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  if (!name) return null
  return {
    id: user.id,
    name,
    avatarUrl: user.picture ?? undefined,
    team: teamName,
    count: user.count ?? undefined,
  }
}

function authUserToOption(user: UserResponse): UserOption {
  const name = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  return {
    id: user.id,
    name: name || user.email,
    avatarUrl: user.picture ?? undefined,
  }
}

function flattenTeamUsers(payload: ShipmentTeamsResponse): UserOption[] {
  const users: UserOption[] = []
  for (const team of payload.teams) {
    for (const user of team.users ?? []) {
      const option = toUserOption(user, team.name)
      if (option) users.push(option)
    }
  }
  return users
}

function resolveMe(
  payload: ShipmentTeamsResponse | undefined,
  authUser: UserResponse | null,
): UserOption | undefined {
  const teamsMe = payload?.me
    ? (toUserOption(payload.me, '') ?? undefined)
    : undefined
  return teamsMe ?? (authUser ? authUserToOption(authUser) : undefined)
}

function buildTeamsParams(
  stage: ShipmentStage | undefined,
  status: string | null | undefined,
  q: string,
) {
  if (!stage && !status && !q) return undefined
  return {
    ...(stage ? { stage } : {}),
    ...(status ? { status } : {}),
    ...(q ? { q } : {}),
  }
}

export interface ToolbarUsersResult {
  users: UserOption[]
  me: UserOption | undefined
  allCount: number | undefined
  /**
   * `false` when the caller lacks the stage's view permission: the org chart
   * still loads, but every `count` is null. Consumers must hide the count
   * column entirely rather than render those nulls as `0`.
   */
  countsAvailable: boolean
}

export function useToolbarUsers(
  stage: ShipmentStage | undefined,
  search: string,
  status?: string | null,
): ToolbarUsersResult {
  const debounced = useDebouncedValue(search, USER_SEARCH_DEBOUNCE_MS).trim()
  const authUser = useAuthStore((s) => s.user)
  const { data } = useListShipmentTeamsApiV1ShipmentsTeamsGet(
    buildTeamsParams(stage, status, debounced),
  )
  return useMemo(() => {
    const payload = data?.data
    const users = payload ? flattenTeamUsers(payload) : []
    const me = resolveMe(payload, authUser)
    // Older responses omit the flag; treat absence as "counts available".
    const countsAvailable = payload?.counts_available ?? true
    return {
      users,
      me,
      allCount: payload?.all_count ?? undefined,
      countsAvailable,
    }
  }, [data, authUser])
}
